// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file DropdownMenuCheckboxItem.tsx
 * @input React, stylex, Item + CheckboxInput + DropdownMenu context + tokens from core
 * @output DropdownMenuCheckboxItem — a standalone checkable menu item.
 * @position Sub-component; used inside DropdownMenu.
 *
 * A menu item that toggles an independent boolean (role="menuitemcheckbox").
 * Unlike CheckboxInput, there is no nested native <input> that participates in
 * accessibility: the row itself owns the role and aria-checked, per the
 * WAI-ARIA menuitemcheckbox pattern. Keyboard navigation (arrows/typeahead) and
 * Enter/Space activation come from the parent DropdownMenu's useListFocus +
 * activation path, which matches menuitemcheckbox alongside plain menuitem rows.
 *
 * The checkbox visual composes the real CheckboxInput primitive so its checkmark
 * matches CheckboxListItem and picks up the standard `checkbox` theming slots.
 * It is purely decorative: the composed control is wrapped in an element that is
 * both `aria-hidden` and `inert`, so it contributes nothing to the row's
 * accessible name, and its native <input> and sr-only label stay out of the tab
 * order and the accessibility tree while pointer clicks fall through to the row
 * — the same shim MultiSelector uses. The row owns the checked state and
 * accessible name. The control size is derived from the menu's item size (a
 * `sm` menu gets the compact control; `md`/`lg` get the standard one) and it
 * swaps to the inline-end of the row on coarse-pointer (touch) devices via CSS
 * `order`, so it lands where selection toggles are conventionally placed on
 * mobile.
 */
import { useCallback } from 'react';
import * as stylex from '@stylexjs/stylex';
import { renderIconSlot } from "../Icon/index.js";
import { CheckboxInput } from "../CheckboxInput/CheckboxInput.js";
import { Item } from "../Item/index.js";
import { useDropdownMenuContext } from "./DropdownMenuContext.js";
import { focusMenuItemOnHover } from "./menuItemHover.js";
import "../theme/tokens.stylex.js";
import { colorVars, spacingVars } from "../theme/tokens.stylex.js";
import { mergeProps, themeProps } from "../utils/index.js";
import { jsx as _jsx } from "react/jsx-runtime";
const styles = {
  root: {
    kzqmXN: "xh8yej3",
    kaIpWk: "x1ws5lxm",
    kMwMTN: "x1tgivj0",
    kWkggS: "xjbqb8w x1c52tdz",
    kkrTdU: "x1ypdohk",
    kI3sdo: "x1a2a7pz",
    $$css: true
  },
  disabled: {
    kSiTet: "xbyyjgo",
    kkrTdU: "x1h6gzvc",
    $$css: true
  }
};
/**
 * A checkable dropdown menu item (role="menuitemcheckbox").
 *
 * Must be used inside a DropdownMenu. Toggles an independent boolean; for a
 * one-of-N choice use DropdownMenuRadioGroup + DropdownMenuRadioItem instead.
 *
 * @example
 * ```
 * import {DropdownMenuCheckboxItem} from '@astryxdesign/core/DropdownMenu';
 * <DropdownMenu button={{label: 'View'}}>
 *   <DropdownMenuCheckboxItem
 *     label="Show archived"
 *     value={showArchived}
 *     onChange={setShowArchived}
 *   />
 * </DropdownMenu>
 * ```
 */
export function DropdownMenuCheckboxItem({
  label,
  description,
  icon,
  value,
  onChange,
  isDisabled = false,
  hasCloseOnSelect = false,
  endContent,
  xstyle,
  className,
  style,
  ...rest
}) {
  const ctx = useDropdownMenuContext();
  const menuSize = ctx?.menuSize ?? 'md';
  const controlSize = menuSize === 'sm' ? 'sm' : 'md';

  // The composed checkbox is decorative and inert, so its label never reaches
  // the accessibility tree — the row's `label` prop provides the announced
  // name. CheckboxInput still requires a string label, so pass one through when
  // the row label is a plain string.
  const checkboxLabel = typeof label === 'string' ? label : '';
  const handleClick = useCallback(() => {
    if (isDisabled) {
      return;
    }
    onChange?.(!value);
    if (hasCloseOnSelect) {
      ctx?.closeMenu();
    }
  }, [isDisabled, onChange, value, hasCloseOnSelect, ctx]);
  const handlePointerMove = useCallback(e => focusMenuItemOnHover(e, isDisabled), [isDisabled]);
  return /*#__PURE__*/_jsx(Item, {
    ...rest,
    role: "menuitemcheckbox",
    "aria-checked": value,
    tabIndex: isDisabled ? undefined : -1,
    onPointerMove: handlePointerMove,
    marker: /*#__PURE__*/_jsx("div", {
      "aria-hidden": "true",
      inert: true,
      ...{
        className: "x78zum5 x2lah0s x47corl x1g77sc7 xozvky x1lziwak x1jymrmb"
      },
      children: /*#__PURE__*/_jsx(CheckboxInput, {
        label: checkboxLabel,
        isLabelHidden: true,
        value: value,
        isDisabled: isDisabled,
        size: controlSize
      })
    }),
    startContent: icon ? renderIconSlot(icon, {
      size: 'sm',
      color: 'secondary'
    }) : undefined,
    label: label,
    description: description,
    endContent: endContent,
    onClick: handleClick,
    isDisabled: isDisabled,
    xstyle: [styles.root, isDisabled && styles.disabled, xstyle],
    ...mergeProps(themeProps('dropdown-menu-item', {
      size: menuSize
    }), {
      className,
      style
    })
  });
}
DropdownMenuCheckboxItem.displayName = 'DropdownMenuCheckboxItem';